import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { AnalyticsProvider } from "./utils/AnalyticsProvider";
import { ErrorBoundary } from "./utils/errorTracking";
import { trackEvent } from "./utils/analytics";

// Custom error fallback component
const ErrorFallback = ({
  error,
  resetError,
}: {
  error: Error;
  resetError: () => void;
}) => (
  <div
    className="error-container"
    style={{ padding: "20px", margin: "20px", border: "1px solid red" }}
  >
    <h2>Something went wrong</h2>
    <p>We've logged this error and our team is working to fix it.</p>
    <button
      onClick={() => {
        // Track error recovery attempt
        trackEvent("error_recovery_attempt", {
          error_message: error.message,
        });
        resetError();
      }}
      style={{
        padding: "8px 16px",
        background: "#1a1a1a",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      Try Again
    </button>
  </div>
);

// Performance monitoring via reportWebVitals
reportWebVitals((metric) => {
  trackEvent("web_vitals", {
    metric_name: metric.name,
    metric_value: metric.value,
    metric_id: metric.id,
    metric_rating: getRatingFromMetric(metric),
  });
});

// Helper to determine performance rating
function getRatingFromMetric(metric: { name: string; value: number }): string {
  switch (metric.name) {
    case "FCP":
      return metric.value < 1800
        ? "good"
        : metric.value < 3000
        ? "needs-improvement"
        : "poor";
    case "LCP":
      return metric.value < 2500
        ? "good"
        : metric.value < 4000
        ? "needs-improvement"
        : "poor";
    case "CLS":
      return metric.value < 0.1
        ? "good"
        : metric.value < 0.25
        ? "needs-improvement"
        : "poor";
    case "FID":
    case "TTFB":
      return metric.value < 100
        ? "good"
        : metric.value < 300
        ? "needs-improvement"
        : "poor";
    default:
      return "unknown";
  }
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <ErrorBoundary errorComponent={ErrorFallback}>
      <AnalyticsProvider
        trackScrollDepth={true}
        trackTimeOnPage={true}
        trackExitIntent={true}
        trackPerformance={true}
      >
        <App />
      </AnalyticsProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
